import { useAuthContext } from "../../auth/state/AuthContext";
import { themeAtom } from "../../shared/atoms";
import { toastError, toastSuccess } from "../../shared/components/ToastContainer";
import WithTooltip from "../../shared/components/WithTooltip";
import { ReusableButton } from "../../voyage-pnl/components/ReusableTableButton";
import { sendSupportContact, supportContactReasons, type SupportContactReason } from "../services/support.service";
import { useAtomValue } from "jotai";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import { twMerge } from "tailwind-merge";

interface SupportContactModalProps {
  onClose: () => void;
}

export const SupportContactModal: React.FC<SupportContactModalProps> = ({ onClose }) => {
  const theme = useAtomValue(themeAtom);
  const { firstname = "", lastname = "", sub: email = "" } = useAuthContext().state.user?.accessToken || {};
  const userName = `${firstname} ${lastname}`.trim() || "Unknown user";
  const userEmail = email || "Unknown email";
  const [reason, setReason] = useState<SupportContactReason | "">("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const canSend = reason !== "" && message.trim().length > 0 && !isSending;

  const handleClose = () => {
    if (!isSending) {
      onClose();
    }
  };

  const handleSend = async () => {
    if (reason === "" || message.trim().length === 0 || isSending) return;

    setIsSending(true);
    try {
      await sendSupportContact({ reason, message });
      toastSuccess("Email successfully sent");
      onClose();
    } catch (error) {
      toastError("Email sending failed");
      console.error("Error sending support email:", error);
      setIsSending(false);
    }
  };

  const portalChildren = (
    <div className="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-sm" onClick={handleClose}>
      <div
        className={twMerge(
          "z-50 flex w-[460px] flex-col rounded-xl border px-6 py-5 shadow-xl",
          theme.uwhcAssistant.border,
          theme.home.tailwind.panelBackground,
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={twMerge("mb-4 text-[18px] font-bold", theme.uwhcAssistant.text)}>Contact support</div>

        <div className="mb-3">
          <label htmlFor="support-contact-name" className={twMerge("mb-1 block text-sm", theme.uwhcAssistant.text)}>
            Name
          </label>
          <input
            id="support-contact-name"
            type="text"
            value={userName}
            readOnly
            className={twMerge(
              "w-full cursor-not-allowed rounded border p-2 text-[14px] focus:outline-none",
              theme.uwhcAssistant.border,
              theme.uwhcAssistant.text,
              theme.home.tailwind.backgound,
            )}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="support-contact-email" className={twMerge("mb-1 block text-sm", theme.uwhcAssistant.text)}>
            Email
          </label>
          <input
            id="support-contact-email"
            type="email"
            value={userEmail}
            readOnly
            className={twMerge(
              "w-full cursor-not-allowed rounded border p-2 text-[14px] focus:outline-none",
              theme.uwhcAssistant.border,
              theme.uwhcAssistant.text,
              theme.home.tailwind.backgound,
            )}
          />
        </div>

        <div className="mb-3">
          <WithTooltip id="support-contact-reason-required" content="Required field" className="mb-1 inline-block">
            <label htmlFor="support-contact-reason" className={twMerge("block text-sm", theme.uwhcAssistant.text)}>
              Reason<sup>*</sup>
            </label>
          </WithTooltip>
          <select
            id="support-contact-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value as SupportContactReason | "")}
            className={twMerge(
              "w-full rounded border p-2 text-[14px] focus:outline-none focus:ring",
              theme.uwhcAssistant.border,
              theme.uwhcAssistant.text,
              theme.home.tailwind.backgound,
            )}
          >
            <option value="">Select reason</option>
            {supportContactReasons.map((supportReason) => (
              <option key={supportReason} value={supportReason}>
                {supportReason}
              </option>
            ))}
          </select>
        </div>

        <div>
          <WithTooltip id="support-contact-message-required" content="Required field" className="mb-1 inline-block">
            <label htmlFor="support-contact-message" className={twMerge("block text-sm", theme.uwhcAssistant.text)}>
              Message<sup>*</sup>
            </label>
          </WithTooltip>
          <textarea
            id="support-contact-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Insert message here..."
            maxLength={5000}
            className={twMerge(
              "h-[150px] w-full resize-none rounded border p-2 text-[14px] focus:outline-none focus:ring",
              theme.uwhcAssistant.border,
              theme.uwhcAssistant.text,
              theme.home.tailwind.backgound,
            )}
          />
        </div>

        <div className="mt-5 flex justify-end">
          <ReusableButton width={80} buttonText="Close" onClickHandler={handleClose} className="mr-3" />
          <ReusableButton
            width={80}
            buttonText={isSending ? "Sending" : "Send"}
            onClickHandler={handleSend}
            disabled={!canSend}
          />
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(portalChildren, document.body);
};
